import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  DataUploadFileResponse,
  FileUploadsOptions,
  FileUploadsResult,
  UploadedFile,
} from './types';
import { hasDetectionFor, isFamilyMembersFile, mapDetectedClients } from './mapDetectedClients';
import { useFileUploadActions } from './useFileUploadActions';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useClient } from 'src/clients/hooks/useClient';
import { useFlags } from 'src/feature-flags/FlagsHook';

export const useFileUploads = ({
  autoRefreshProcessing = false,
  clientId: overrideClientId,
  familyId: overrideFamilyId,
}: FileUploadsOptions = {}): FileUploadsResult => {
  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);
  const { clientId: defaultClientId } = useCurrentClientIfAvailable();
  const clientId = overrideClientId ?? defaultClientId;
  const actions = useFileUploadActions({ clientId });
  const { flagEnabledFamilyMemberRecordingReview } = useFlags();
  const { client } = useClient(clientId);
  const clientFamilyId = client?.family?.id ?? client?.familyId;
  const showsFamilyRecordings =
    flagEnabledFamilyMemberRecordingReview && !overrideFamilyId && !!clientId && !!clientFamilyId;

  const queryKey = overrideFamilyId
    ? ['families', overrideFamilyId, 'data-upload-files']
    : ['clients', clientId, 'data-upload-files'];

  const {
    data,
    isLoading: isLoadingFiles,
    isError: isLoadError,
    refetch: refetchFiles,
  } = useQuery<DataUploadFileResponse[], Error, UploadedFile[]>({
    queryKey,
    refetchInterval: refetchInterval || undefined,
    select: mapDataUploadFiles,
  });

  const { data: familyFiles } = useQuery<DataUploadFileResponse[], Error, UploadedFile[]>({
    queryKey: ['families', clientFamilyId, 'data-upload-files'],
    enabled: showsFamilyRecordings,
    select: mapDataUploadFiles,
  });

  const files = useMemo(
    () =>
      showsFamilyRecordings && clientId
        ? asSeenByClient(data ?? [], familyFiles ?? [], clientId)
        : (data ?? []),
    [data, familyFiles, showsFamilyRecordings, clientId],
  );

  useEffect(() => {
    if (
      autoRefreshProcessing &&
      files.some(file => ['PROCESSING', 'PENDING'].includes(file.backgroundProcess?.status || ''))
    ) {
      setRefetchInterval(5000);
    } else {
      setRefetchInterval(false);
    }
  }, [files, autoRefreshProcessing]);

  const fileIsAdded = (file: UploadedFile): boolean => {
    return !file.isFolder && (!file.isResolved || !file.file.isFiled);
  };

  const filesToAdd = useMemo(() => {
    return files.filter(fileIsAdded);
  }, [files]);

  const allOtherFiles = useMemo(() => {
    return files.filter(file => !fileIsAdded(file));
  }, [files]);

  return {
    files,
    filesToAdd,
    allOtherFiles,
    ...actions,
    isLoading: isLoadingFiles,
    error: isLoadError ? new Error('Failed to load files') : actions.error,
    refetchFiles,
  };
};

function mapDataUploadFiles(data: DataUploadFileResponse[]) {
  return data.map(file => ({
    id: file.id,
    file: {
      id: file.file.id,
      fileName: file.file.fileName,
      fileSize: file.file.fileSize,
      fileType: file.file.fileType,
      isFolder: file.isFolder,
      isResolved: file.isResolved,
      isFiled: file.file.isFiled || false,
      isTemporary: file.file.isTemporary || false,
      createdAt: file.createdAt,
      parentId: file.parentId,
      templateId: file.file.templateId,
    },
    clientId: file.owner?.kind === 'client' ? file.owner.clientId : (file.clientId ?? undefined),
    isFolder: file.isFolder,
    isResolved: file.isResolved,
    createdAt: file.createdAt,
    parentId: file.parentId,
    templateId: file.file.templateId,
    backgroundProcess: file.backgroundProcess,
    fileName: file.file.fileName,
    fileSize: file.file.fileSize,
    fileType: file.file.fileType,
    dataUploadModel: file.dataUploadModel,
    documentCategory: file.documentCategory,
    documentDate: file.documentDate,
    detectedClients: mapDetectedClients(file.detectedClients),
  }));
}

// A family meeting's recording is stored under one member but carries a detection for each
// member, so every member sees it among their own documents with only their own part.
function asSeenByClient(ownFiles: UploadedFile[], familyFiles: UploadedFile[], clientId: string) {
  const sharedFiles = familyFiles
    .filter(file => isFamilyMembersFile(file, clientId))
    .filter(file => hasDetectionFor(file, clientId));
  return [...ownFiles, ...sharedFiles.map(file => asSharedFile(file, clientId))].map(file => ({
    ...file,
    detectedClients: file.detectedClients.filter(detected => detected.id === clientId),
  }));
}

function asSharedFile(file: UploadedFile, clientId: string): UploadedFile {
  const isProcessed = file.detectedClients.some(
    detected => detected.id === clientId && detected.isProcessed,
  );
  return {
    ...file,
    isResolved: isProcessed,
    parentId: undefined,
    file: { ...file.file, isResolved: isProcessed, isFiled: false, parentId: undefined },
  };
}
